import { jsx as _jsx } from "react/jsx-runtime";
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { useComposerState } from '#pplx/frontend/composer-sdk/internal/input-state/useComposerState.js';
import { EXTERNAL_VALUE_UPDATE_TAG } from '#pplx/frontend/composer-sdk/internal/updateTags.js';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createRangeSelection, $getRoot, $getSelection, $isElementNode, $isRangeSelection, $isTextNode, $setSelection, } from 'lexical';
import { isEqual } from 'lodash-es';
import { useEffect } from 'react';
/**
 * Saves the caret's position so it can be restored after the draft is replaced.
 * Uses a path from the document root because replacing the draft creates new node keys.
 *
 * @returns The caret's location, or the end when there is no selection or the selection is not a single caret.
 */
function $captureCaretIfExists() {
    const selection = $getSelection();
    if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
        return { type: 'end' };
    }
    let node = selection.anchor.getNode();
    const path = [];
    for (let parent = node.getParent(); parent !== null; parent = node.getParent()) {
        path.unshift(node.getIndexWithinParent());
        node = parent;
    }
    return {
        path,
        offset: selection.anchor.offset,
        type: selection.anchor.type,
    };
}
// Walks the new tree along the captured path and reinstates the caret.
// Returns false if the new document has no valid spot at that path; the
// caller falls back to collapsing to end.
function $restoreCaret({ path, offset, type, }) {
    let node = $getRoot();
    for (const childIndex of path) {
        if (!$isElementNode(node)) {
            return false;
        }
        const child = node.getChildAtIndex(childIndex);
        if (child === null) {
            return false;
        }
        node = child;
    }
    const isValidTextPoint = type === 'text' && $isTextNode(node) && offset <= node.getTextContentSize();
    const isValidElementPoint = type === 'element' && $isElementNode(node) && offset <= node.getChildrenSize();
    if (!isValidTextPoint && !isValidElementPoint) {
        return false;
    }
    const selection = $createRangeSelection();
    selection.anchor.set(node.getKey(), offset, type);
    selection.focus.set(node.getKey(), offset, type);
    $setSelection(selection);
    return true;
}
function ControlledValueSynchronizer({ autoFocus, externalValueCaret, value, }) {
    const [editor] = useLexicalComposerContext();
    useEffect(() => {
        if (isEqual(editor.getEditorState().toJSON(), value)) {
            return;
        }
        // Replace editor state with the caller's value. The tag marks this update
        // as externally driven so downstream subscriptions can skip re-emitting it.
        editor.update(() => {
            let caret = { type: 'blurred' };
            if (autoFocus || editor.getRootElement() === document.activeElement) {
                switch (externalValueCaret) {
                    case 'preserve':
                        caret = $captureCaretIfExists();
                        break;
                    case 'end':
                        caret = { type: 'end' };
                        break;
                    default:
                        assertUnreachableValue(externalValueCaret);
                }
            }
            const nextEditorState = editor.parseEditorState(value, () => {
                switch (caret.type) {
                    case 'blurred':
                        // A blurred controlled Composer must stay blurred when its owner replaces the
                        // draft (for example after submit); retaining Lexical's stale selection focuses it.
                        $setSelection(null);
                        return;
                    case 'end':
                        $getRoot().selectEnd();
                        return;
                    case 'element':
                    case 'text':
                        if (!$restoreCaret(caret)) {
                            $getRoot().selectEnd();
                        }
                        return;
                    default:
                        assertUnreachableValue(caret);
                }
            });
            editor.setEditorState(nextEditorState);
        }, { tag: EXTERNAL_VALUE_UPDATE_TAG });
    }, [autoFocus, editor, externalValueCaret, value]);
    return null;
}
export function ControlledValuePlugin({ autoFocus, externalValueCaret, }) {
    const state = useComposerState();
    // Mirrors the consumer's controlled value into the editor whenever it
    // changes. Skips when there's nothing to mirror (uncontrolled) or when the
    // editor already matches, so user edits aren't clobbered on every render.
    if (state.draftMode !== 'controlled') {
        return null;
    }
    const { value } = state;
    return (_jsx(ControlledValueSynchronizer, { autoFocus: autoFocus, externalValueCaret: externalValueCaret, value: value }));
}
//# sourceMappingURL=ControlledValuePlugin.js.map